import React, {
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import { API_URL } from '../config.js';
import './Images3Question.css';

// Normaliza "sí"
const isYes = (v) =>
  String(v ?? '')
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .match(/^(si|sí|true|1|ok)$/);

// Detecta "info" en la celda de Puntaje
const isInfoValue = (v) =>
  String(v ?? '')
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim() === 'info';

// Determina nivel desde la ruta
function inferNivelFromPath() {
  const p =
    (
      typeof window !== 'undefined'
        ? window.location.pathname
        : ''
    ).toLowerCase();

  if (p.includes('repaso')) {
    return 'Repaso';
  }

  if (
    p.includes('basico') ||
    p.includes('básico')
  ) {
    return 'Basico';
  }

  if (p.includes('medio')) {
    return 'Medio';
  }

  if (p.includes('avanz')) {
    return 'Avanzado';
  }

  return 'Desconocido';
}

// Registrar respuesta sin bloquear la navegación.
// Ahora también valida errores HTTP del backend.
function registrarRespuesta({
  pregunta,
  letra,
  es_correcta
}) {
  try {
    const session_id =
      localStorage.getItem('sesion') ||
      `SESS_${Date.now()}`;

    localStorage.setItem(
      'sesion',
      session_id
    );

    const nombreUsuario =
      localStorage.getItem('usuario') ||
      'Jugador';

    const nivel =
      inferNivelFromPath();

    const device =
      /Mobi/.test(navigator.userAgent)
        ? 'mobile'
        : 'desktop';

    fetch(`${API_URL}/api/answer`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        session_id,
        nombreUsuario,
        nivel,
        pregunta_id:
          pregunta?.ID,
        tipo_pregunta:
          '3_imagenes',
        respuesta_usuario:
          letra,
        es_correcta:
          es_correcta ?? null,
        device,
        ts: Date.now()
      })
    })
      .then(async (response) => {
        if (response.ok) {
          return;
        }

        const detalle =
          await response
            .text()
            .catch(() => '');

        console.error(
          '❌ Error HTTP registrando respuesta ' +
          '(3Imagenes):',
          response.status,
          detalle
        );
      })
      .catch((error) => {
        console.error(
          '❌ Error de red registrando respuesta ' +
          '(3Imagenes):',
          error
        );
      });
  } catch (error) {
    console.error(
      '❌ Error registrando respuesta ' +
      '(3Imagenes):',
      error
    );
  }
}

export default function Images3Question({
  pregunta,
  onNext,
  onScore,
  onLoseLife,
  reemplazarNombre,
  resolveImg3,
  getLives,
  noLivesId = '1000'
}) {
  const [modalSrc, setModalSrc] =
    useState(null);

  const [busy, setBusy] =
    useState(false);

  // Estado de feedback modal
  const [mostrarFeedback, setMostrarFeedback] =
    useState(false);

  const [textoFeedback, setTextoFeedback] =
    useState('');

  const [esCorrecto, setEsCorrecto] =
    useState(null);

  const [esInfo, setEsInfo] =
    useState(false);

  const [siguienteID, setSiguienteID] =
    useState(null);

  // Evita que dos clics ingresen antes de que React
  // alcance a actualizar el estado busy.
  const procesandoRef =
    useRef(false);

  // Conserva siempre la versión más reciente de onNext.
  const onNextRef =
    useRef(onNext);

  // Permite cancelar temporizadores antiguos.
  const timerRef =
    useRef(null);

  useEffect(() => {
    onNextRef.current =
      onNext;
  }, [onNext]);

  const limpiarTimer = () => {
    if (timerRef.current) {
      clearTimeout(
        timerRef.current
      );

      timerRef.current =
        null;
    }
  };

  const restaurarPreguntaActual = () => {
    limpiarTimer();

    procesandoRef.current =
      false;

    setBusy(false);
    setMostrarFeedback(false);
    setTextoFeedback('');
    setEsCorrecto(null);
    setEsInfo(false);
    setSiguienteID(null);
  };

  // Limpia el estado cuando cambia la pregunta.
  useEffect(() => {
    limpiarTimer();

    procesandoRef.current =
      false;

    setModalSrc(null);
    setBusy(false);
    setMostrarFeedback(false);
    setTextoFeedback('');
    setEsCorrecto(null);
    setEsInfo(false);
    setSiguienteID(null);

    return () => {
      limpiarTimer();
    };
  }, [pregunta?.ID]);

  const esperarOK =
    useMemo(
      () =>
        !!isYes(
          pregunta?.EsperarOK
        ),
      [pregunta]
    );

  const opts =
    useMemo(
      () => [
        {
          key: 'A',
          title:
            pregunta?.OpcionA ||
            'Opción A',
          img:
            resolveImg3?.(
              pregunta?.ImagenA
            ),
          rawScore:
            pregunta?.PuntajeA,
          score:
            parseInt(
              pregunta?.PuntajeA ??
              '0',
              10
            ),
          feedback:
            pregunta?.FeedbackA ||
            '',
          next:
            pregunta?.SiguienteIDA
        },
        {
          key: 'B',
          title:
            pregunta?.OpcionB ||
            'Opción B',
          img:
            resolveImg3?.(
              pregunta?.ImagenB
            ),
          rawScore:
            pregunta?.PuntajeB,
          score:
            parseInt(
              pregunta?.PuntajeB ??
              '0',
              10
            ),
          feedback:
            pregunta?.FeedbackB ||
            '',
          next:
            pregunta?.SiguienteIDB
        },
        {
          key: 'C',
          title:
            pregunta?.OpcionC ||
            'Opción C',
          img:
            resolveImg3?.(
              pregunta?.ImagenC
            ),
          rawScore:
            pregunta?.PuntajeC,
          score:
            parseInt(
              pregunta?.PuntajeC ??
              '0',
              10
            ),
          feedback:
            pregunta?.FeedbackC ||
            '',
          next:
            pregunta?.SiguienteIDC
        }
      ],
      [
        pregunta,
        resolveImg3
      ]
    );

  // Texto inferior — prioriza HTML formateado desde backend
  const captionHtml =
    useMemo(() => {
      const raw =
        (
          pregunta?.OpcionD_HTML ||
          pregunta?.OpcionD ||
          ''
        ).trim();

      return reemplazarNombre
        ? reemplazarNombre(raw)
        : raw;
    }, [
      pregunta,
      reemplazarNombre
    ]);

  const avanzarSeguro = (next) => {
    limpiarTimer();

    const siguienteNormalizado =
      String(next ?? '').trim();

    const preguntaActualID =
      String(
        pregunta?.ID ?? ''
      ).trim();

    /*
     * Si no existe un siguiente ID o este apunta
     * nuevamente a la pregunta actual, se permite
     * seleccionar otra alternativa.
     */
    if (
      !siguienteNormalizado ||
      siguienteNormalizado === preguntaActualID
    ) {
      restaurarPreguntaActual();
      return;
    }

    /*
     * Cuando realmente corresponde cambiar de pregunta,
     * se mantiene el bloqueo hasta que cambie pregunta.ID.
     */
    if (
      typeof onNextRef.current ===
      'function'
    ) {
      onNextRef.current(
        siguienteNormalizado
      );

      return;
    }

    restaurarPreguntaActual();
  };

  const handleSelect = (opt) => {
    if (
      procesandoRef.current ||
      busy ||
      mostrarFeedback
    ) {
      return;
    }

    procesandoRef.current =
      true;

    setBusy(true);

    const info =
      isInfoValue(
        opt.rawScore
      );

    const rawFb =
      String(
        opt.feedback ||
        ''
      ).trim();

    const fb =
      reemplazarNombre
        ? reemplazarNombre(rawFb)
        : rawFb;

    // Se conserva el comportamiento original:
    // si no hay siguiente ID configurado, se interpreta como fin.
    const next =
      opt.next || 'fin';

    setSiguienteID(
      next
    );

    // Rama INFO
    if (info) {
      registrarRespuesta({
        pregunta,
        letra:
          opt.key,
        es_correcta:
          'Info'
      });

      setEsInfo(true);
      setEsCorrecto(null);
      setTextoFeedback(fb);

      if (fb) {
        setMostrarFeedback(true);
      }

      if (!fb) {
        avanzarSeguro(next);
      } else if (!esperarOK) {
        timerRef.current =
          setTimeout(() => {
            setMostrarFeedback(false);
            avanzarSeguro(next);
          }, 2500);
      }

      return;
    }

    // Rama numérica
    const correcto =
      Number.isFinite(
        opt.score
      )
        ? opt.score > 0
        : false;

    registrarRespuesta({
      pregunta,
      letra:
        opt.key,
      es_correcta:
        correcto
          ? true
          : false
    });

    setEsInfo(false);
    setEsCorrecto(
      correcto
    );

    if (
      Number.isFinite(
        opt.score
      ) &&
      typeof onScore ===
        'function'
    ) {
      onScore(
        opt.score
      );
    }

    if (!correcto) {
      /*
       * Se leen las vidas antes de ejecutar onLoseLife.
       * Esto evita depender del retorno asincrónico
       * de setVidas() en el componente padre.
       */
      let vidasAntes =
        null;

      if (
        typeof getLives ===
        'function'
      ) {
        const actuales =
          Number(
            getLives()
          );

        if (
          Number.isFinite(
            actuales
          )
        ) {
          vidasAntes =
            actuales;
        }
      }

      let retornoPadre =
        null;

      if (
        typeof onLoseLife ===
        'function'
      ) {
        retornoPadre =
          onLoseLife();
      }

      let vidasRestantes =
        null;

      if (
        Number.isFinite(
          retornoPadre
        )
      ) {
        vidasRestantes =
          retornoPadre;
      } else if (
        Number.isFinite(
          vidasAntes
        )
      ) {
        vidasRestantes =
          Math.max(
            vidasAntes - 1,
            0
          );
      }

      if (
        Number.isFinite(
          vidasRestantes
        ) &&
        vidasRestantes <= 0
      ) {
        avanzarSeguro(
          noLivesId
        );

        return;
      }

      if (fb) {
        setTextoFeedback(
          fb
        );

        setMostrarFeedback(
          true
        );

        if (!esperarOK) {
          timerRef.current =
            setTimeout(() => {
              restaurarPreguntaActual();
            }, 2500);
        }
      } else {
        restaurarPreguntaActual();
      }

      return;
    }

    // Respuesta correcta
    if (!fb) {
      avanzarSeguro(
        next
      );

      return;
    }

    setTextoFeedback(
      fb
    );

    setMostrarFeedback(
      true
    );

    if (!esperarOK) {
      timerRef.current =
        setTimeout(() => {
          setMostrarFeedback(false);
          avanzarSeguro(next);
        }, 2500);
    }
  };

  const cerrarFeedback = () => {
    restaurarPreguntaActual();
  };

  const continuarOK = () => {
    if (
      esCorrecto ||
      esInfo
    ) {
      setMostrarFeedback(false);

      avanzarSeguro(
        siguienteID
      );

      return;
    }

    cerrarFeedback();
  };

  return (
    <div className="imgs3-wrapper">
      {/* 3 miniaturas */}
      <div className="imgs3-row">
        {opts.map((opt) => (
          <div
            key={opt.key}
            className="imgs3-card"
          >
            <div className="imgs3-badge">
              {opt.key}
            </div>

            <button
              className="imgs3-thumbbtn"
              onClick={() =>
                opt.img &&
                setModalSrc(opt.img)
              }
              disabled={!opt.img}
              aria-label={
                `Ampliar imagen ${opt.key}`
              }
            >
              {opt.img ? (
                <img
                  src={opt.img}
                  alt={opt.title}
                  className="imgs3-thumb"
                />
              ) : (
                <div className="imgs3-thumb placeholder">
                  Sin imagen
                </div>
              )}
            </button>
          </div>
        ))}
      </div>

      {/* Botones de alternativas */}
      <div className="imgs3-actions">
        {opts.map((opt) => (
          <button
            key={opt.key}
            className="imgs3-optionBtn"
            onClick={() =>
              handleSelect(opt)
            }
            disabled={
              busy ||
              mostrarFeedback
            }
          >
            {opt.title}
          </button>
        ))}
      </div>

      {/* Texto inferior */}
      {captionHtml?.trim() && (
        <div className="imgs3-caption">
          <div
            className="imgs3-caption-inner"
            dangerouslySetInnerHTML={{
              __html:
                captionHtml
            }}
          />
        </div>
      )}

      {/* Modal feedback */}
      {mostrarFeedback &&
        textoFeedback && (
          <div
            className="altg-fb-modal"
            onClick={() =>
              esperarOK
                ? null
                : (
                    esCorrecto ||
                    esInfo
                      ? null
                      : cerrarFeedback()
                  )
            }
          >
            <div
              className="altg-fb-card"
              onClick={(e) =>
                e.stopPropagation()
              }
            >
              <p
                dangerouslySetInnerHTML={{
                  __html:
                    textoFeedback
                }}
              />

              {esperarOK && (
                <button
                  className={
                    `altg-btn-ok ${
                      esCorrecto ||
                      esInfo
                        ? 'correcto'
                        : 'incorrecto'
                    }`
                  }
                  onClick={
                    continuarOK
                  }
                >
                  {
                    esCorrecto ||
                    esInfo
                      ? 'Continuar'
                      : 'OK'
                  }
                </button>
              )}
            </div>
          </div>
        )}

      {/* Modal de imagen ampliada */}
      {modalSrc && (
        <div
          className="imgs3-modal"
          onClick={() =>
            setModalSrc(null)
          }
        >
          <img
            src={modalSrc}
            alt="Ampliada"
            className="imgs3-modalImg"
          />

          <button
            className="imgs3-close"
            onClick={() =>
              setModalSrc(null)
            }
          >
            ✖
          </button>
        </div>
      )}
    </div>
  );
}